import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../../provider/AuthProvider';
import {
  getConversations,
  getMessages,
  sendMessage,
  Conversation,
  Message,
} from '../../services/messageService';
import SheetLoading from '../../components/shared/ui/SheetLoading';

const MessagesPage: React.FC = () => {
  const { conversationId } = useParams<{ conversationId?: string }>();
  const { user } = useAuth();
  const navigate = useNavigate();
  const messagesEndRef = useRef<HTMLDivElement>(null);

  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [activeConversation, setActiveConversation] = useState<Conversation | null>(null);
  const [messages, setMessages] = useState<Message[]>([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [sendingMessage, setSendingMessage] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Fetch conversations
  useEffect(() => {
    const fetchConversations = async () => {
      try {
        const data = await getConversations();
        setConversations(data);

        // If conversationId in URL, set it as active
        if (conversationId) {
          const active = data.find(c => c._id === conversationId);
          if (active) {
            setActiveConversation(active);
          }
        }
      } catch (err) {
        console.error('Failed to fetch conversations:', err);
        setError('Failed to load conversations');
      } finally {
        setLoading(false);
      }
    };

    fetchConversations();
  }, [conversationId]);

  // Fetch messages when active conversation changes
  useEffect(() => {
    const fetchMessages = async () => {
      if (!activeConversation) return;

      try {
        const data = await getMessages(activeConversation._id);
        setMessages(data.messages);
      } catch (err) {
        console.error('Failed to fetch messages:', err);
      }
    };

    fetchMessages();
  }, [activeConversation]);

  // Scroll to bottom when new messages arrive
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const handleSelectConversation = (conv: Conversation) => {
    setActiveConversation(conv);
    navigate(`/messages/${conv._id}`);
  };

  const handleSendMessage = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newMessage.trim() || !activeConversation || sendingMessage) return;

    setSendingMessage(true);
    try {
      const message = await sendMessage(activeConversation._id, newMessage.trim());
      setMessages([...messages, message]);
      setNewMessage('');

      // Update conversation's last message
      setConversations(conversations.map(c =>
        c._id === activeConversation._id
          ? { ...c, lastMessage: newMessage.trim(), lastMessageAt: new Date().toISOString() }
          : c
      ));
    } catch (err) {
      console.error('Failed to send message:', err);
      alert('Failed to send message');
    } finally {
      setSendingMessage(false);
    }
  };

  const getOtherParticipant = (conv: Conversation) => {
    return conv.participants.find(p => p._id !== user?.id);
  };

  const getSenderName = (msg: Message): string => {
    if (typeof msg.senderId === 'string') {
      return msg.senderId === user?.id ? 'You' : 'Unknown';
    }
    return msg.senderId._id === user?.id ? 'You' : `${msg.senderId.firstName} ${msg.senderId.lastName}`;
  };

  const isOwnMessage = (msg: Message): boolean => {
    if (typeof msg.senderId === 'string') {
      return msg.senderId === user?.id;
    }
    return msg.senderId._id === user?.id;
  };

  if (!user) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <p>Please log in to view messages</p>
      </div>
    );
  }

  if (loading) {
    return (
      <SheetLoading text="Loading messages…" />
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 py-8">
        <h1 className="text-2xl font-bold mb-6" style={{ color: 'var(--twc-ink)' }}>
          Messages
        </h1>

        {error && (
          <div className="mb-4 p-4 bg-red-50 text-red-700 rounded-lg">
            {error}
          </div>
        )}

        <div className="bg-white rounded-2xl shadow-lg overflow-hidden" style={{ height: 'calc(100vh - 200px)' }}>
          <div className="flex h-full">
            {/* Conversations List */}
            <div className="w-1/3 border-r overflow-y-auto">
              {conversations.length === 0 ? (
                <div className="p-6 text-center text-gray-500">
                  <div className="text-4xl mb-2">No conversations yet</div>
                  <p className="text-sm">Start a conversation by contacting an employer or applicant</p>
                </div>
              ) : (
                conversations.map((conv) => {
                  const other = getOtherParticipant(conv);
                  return (
                    <div
                      key={conv._id}
                      onClick={() => handleSelectConversation(conv)}
                      className={`p-4 border-b cursor-pointer hover:bg-gray-50 transition-colors ${
                        activeConversation?._id === conv._id ? 'bg-blue-50' : ''
                      }`}
                    >
                      <div className="flex items-start gap-3">
                        <div
                          className="w-10 h-10 rounded-full flex items-center justify-center text-white font-bold"
                          style={{ backgroundColor: 'var(--twc-cambridge)' }}
                        >
                          {other?.firstName?.[0] || '?'}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center justify-between">
                            <h3 className="font-semibold text-sm truncate">
                              {other?.firstName} {other?.lastName}
                            </h3>
                            {conv.unreadCount && conv.unreadCount > 0 && (
                              <span
                                className="w-5 h-5 rounded-full text-xs flex items-center justify-center text-white"
                                style={{ backgroundColor: 'var(--twc-redwood)' }}
                              >
                                {conv.unreadCount}
                              </span>
                            )}
                          </div>
                          <p className="text-xs text-gray-500">
                            {other?.role === 'employer' ? other?.uniName : other?.profession}
                          </p>
                          {conv.jobId && (
                            <p className="text-xs text-gray-400 mt-1">
                              Re: {conv.jobId.title}
                            </p>
                          )}
                          {conv.lastMessage && (
                            <p className="text-sm text-gray-600 truncate mt-1">
                              {conv.lastMessage}
                            </p>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })
              )}
            </div>

            {/* Messages Area */}
            <div className="flex-1 flex flex-col">
              {activeConversation ? (
                <>
                  {/* Header */}
                  <div className="p-4 border-b bg-gray-50">
                    <div className="flex items-center gap-3">
                      {(() => {
                        const other = getOtherParticipant(activeConversation);
                        return (
                          <>
                            <div
                              className="w-10 h-10 rounded-full flex items-center justify-center text-white font-bold"
                              style={{ backgroundColor: 'var(--twc-cambridge)' }}
                            >
                              {other?.firstName?.[0] || '?'}
                            </div>
                            <div>
                              <h3 className="font-semibold">
                                {other?.firstName} {other?.lastName}
                              </h3>
                              <p className="text-sm text-gray-500">
                                {other?.role === 'employer' ? other?.uniName : other?.profession}
                              </p>
                            </div>
                          </>
                        );
                      })()}
                    </div>
                    {activeConversation.jobId && (
                      <p className="text-sm text-gray-500 mt-2">
                        Regarding: {activeConversation.jobId.title} at {activeConversation.jobId.company}
                      </p>
                    )}
                  </div>

                  {/* Messages */}
                  <div className="flex-1 overflow-y-auto p-4 space-y-4">
                    {messages.map((msg) => (
                      <div
                        key={msg._id}
                        className={`flex ${isOwnMessage(msg) ? 'justify-end' : 'justify-start'}`}
                      >
                        <div
                          className={`max-w-[70%] rounded-2xl px-4 py-2 ${
                            isOwnMessage(msg)
                              ? 'bg-blue-600 text-white'
                              : 'bg-gray-100 text-gray-900'
                          }`}
                        >
                          <p className="text-sm">{msg.content}</p>
                          <p
                            className={`text-xs mt-1 ${
                              isOwnMessage(msg) ? 'text-blue-200' : 'text-gray-500'
                            }`}
                          >
                            {new Date(msg.createdAt).toLocaleTimeString([], {
                              hour: '2-digit',
                              minute: '2-digit',
                            })}
                          </p>
                        </div>
                      </div>
                    ))}
                    <div ref={messagesEndRef} />
                  </div>

                  {/* Input */}
                  <form onSubmit={handleSendMessage} className="p-4 border-t bg-gray-50">
                    <div className="flex gap-3">
                      <input
                        type="text"
                        value={newMessage}
                        onChange={(e) => setNewMessage(e.target.value)}
                        placeholder="Type a message..."
                        className="flex-1 px-4 py-2 border rounded-full focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                      <button
                        type="submit"
                        disabled={!newMessage.trim() || sendingMessage}
                        className="px-6 py-2 rounded-full text-white font-medium disabled:opacity-50 transition-colors"
                        style={{ backgroundColor: 'var(--twc-cambridge)' }}
                      >
                        {sendingMessage ? 'Sending...' : 'Send'}
                      </button>
                    </div>
                  </form>
                </>
              ) : (
                <div className="flex-1 flex items-center justify-center text-gray-500">
                  <div className="text-center">
                    <div className="text-6xl mb-4">Select a conversation</div>
                    <p>Choose a conversation from the list to start messaging</p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MessagesPage;
